'use client';
import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  Box,
  Stack,
  Avatar,
  AppBar,
  Toolbar,
  Slide
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function DetailDialog({
  title,
  subtitle,
  icon,
  open,
  onClose,
  children,
  maxWidth = 'sm',
  fullHeight = false,
  showDivider = true,
  fullScreenMode = false
}) {
  if (fullScreenMode) {
    return (
      <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
        <AppBar sx={{ position: 'relative', backgroundColor: '#ffff' }}>
          <Toolbar>
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: 'primary.main',
                width: 35,
                height: 35,
                boxShadow: '0 6px 18px rgba(25,118,210,0.12)'
              }}
            >
              {icon || <InsertDriveFileIcon sx={{ color: '#fff', fontSize: 24 }} />}
            </Avatar>
            <Typography sx={{ ml: 2, flex: 1, color: 'primary.900' }} variant="h4" component="div">
              {title}
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 1, overflowY: 'auto', height: '100%' }}>
          {subtitle && (
            <Typography variant="body2" color="text.textSecondary" sx={{ mb: 2 }}>
              {subtitle}
            </Typography>
          )}
          {children}
        </Box>
        <AppBar sx={{ position: 'relative', backgroundColor: 'transparent' }}>
          <Toolbar sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          </Toolbar>
        </AppBar>
      </Dialog>
    );
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="detail-dialog-title"
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box>
            <DialogTitle id="form-dialog-title" sx={{ m: 0, p: 0, fontWeight: 700, fontSize: '1.125rem' }}>
              {title}
            </DialogTitle>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {showDivider && <Divider />}

      <DialogContent
        dividers
        sx={{
          px: 4,
          py: 3,
          backgroundColor: 'background.paper',
          maxHeight: fullHeight ? 'calc(100vh - 220px)' : '60vh',
          overflowY: 'auto'
        }}
      >
        {children}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', p: 2.5, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderRadius: 1,
            px: 3
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DetailDialog.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.node,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  fullHeight: PropTypes.bool,
  showDivider: PropTypes.bool,
  fullScreenMode: PropTypes.bool
};

DetailDialog.defaultProps = {
  subtitle: '',
  icon: null,
  children: null,
  maxWidth: 'sm',
  fullHeight: false,
  showDivider: true,
  fullScreenMode: false
};
